/* eslint-disable no-console */
// @Library
import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

// @Redux
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/reducers';
import { useAppDispatch } from '../../redux/store';
import { getBudgetAnalysis } from '../../redux/slices/budgets/actions-async';
import { axiosEstimates } from '../../utils';
import { CalculatedCommision, EarningsDetail, IBudgetById } from '../../models';

interface TotalData {
  toPay: number;
  receivable: number;
  benefit: number;
  commision: number;
  utility: number;
}

const initTotalData: TotalData = {
  toPay: 0,
  receivable: 0,
  benefit: 0,
  commision: 0,
  utility: 0,
};

export function useBudgetComissionView(_id?: string) {
  const { id } = useParams<{ id: string }>();
  const [totalData, setTotalData] = useState<TotalData>(initTotalData);
  const { budget, loading } = useSelector((state: RootState) => state.budgets);
  const [budgetCommission, setbudgetCommission] = useState<IBudgetById>({
    budget: {
      id: 0,
      incoterms: '',
      canEdit: false,
      code: '',
      status: '',
      idSeller: 0,
      sellerName: '',
      idCustomer: 0,
      customerName: '',
      createdBy: 0,
      amountOfIva: 0,
      amountOfSubtotal: 0,
      amountOfTotal: 0,
      expirationDate: '',
      createdAt: '',
      updatedAt: '',
      servicesCount: 0,
      type: 0,
      idUser: 0,
      haveBill: false,
      documents: [],
      comments: [],
    },
    seller: {
      id: 0,
      name: '',
      email: '',
    },
    customer: {
      id: 0,
      name: '',
      email: '',
      country: '',
      taxId: '',
      phone: '',
      address: '',
      postalCode: '',
      state: '',
      city: '',
      commission: 0,
    },
    shipments: [],
  });
  const [client, setClient] = useState<any>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['common']);

  useEffect(() => {
    dispatch(getBudgetAnalysis(parseInt(_id || id || '0')))
      .unwrap()
      .catch((error: any) => {
        // Sin permiso (403 del API) se avisa; esta vista vive dentro de la factura, por eso no se sale de ella.
        if (error?.message === 'forbidden') toast.error(t('general.noPermission', { ns: 'common' }));
      });
  }, []);

  useEffect(() => {
    if (budget) {
      const toPay = budget?.suppliers?.reduce((prev, sup) => prev + sup.subTotalToPay, 0) ?? 0;

      const receivable = budget?.suppliers?.reduce((prev, sup) => prev + sup.subTotalReceivable, 0) ?? 0;

      // const commision = budget.suppliers.reduce((prev, sup) => prev + sup.totalCommission, 0);

      const num = (receivable * 100 - toPay * 100) / 100;
      const benefit = Math.round((num + Number.EPSILON) * 100) / 100;
      const commision = benefit * 0.1;

      setTotalData({
        toPay,
        receivable,
        benefit,
        commision,
        utility: benefit - commision,
      });
    }
  }, [budget]);

  const handleGet = async () => {
    try {
      const response = await axiosEstimates.get<IBudgetById>('budget-commission/' + (_id || id));
      // La comisión se pide al API de Estimates, que la consulta en Exports con su llave interna.
      const commision = await axiosEstimates.get<CalculatedCommision>('commission', {
        params: { user_id: response.data.budget.idSeller },
      });
      const iBudgetById = response.data;
      setbudgetCommission({ ...iBudgetById, commission: commision.data });
      setClient(response.data.customer.name);
    } catch (error) {
      console.error(error);
    }
  };
  useEffect(() => {
    handleGet();
  }, []);

  const handleback = () => {
    navigate('/budgets');
  };
  const handlePrint = () => {
    navigate('/budgets/print/' + id);
  };

  async function createEarningsDetail(input: {
    payable: number;
    receivable: number;
    earnings: number;
    commission: number;
    utilities: number;
    budgetId: number;
  }): Promise<EarningsDetail> {
    try {
      const response = await axiosEstimates.post<EarningsDetail>('budget-commission', input);

      return response.data;
    } catch (error) {
      throw new Error(`Error creating EarningsDetail: ${error}`);
    }
  }

  return { budgetCommission, budget, loading, handleback, handlePrint, totalData, client, createEarningsDetail };
}
